import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Wallet, Palette, Users } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "designer" });
  const [busy, setBusy] = useState(false);
  const [slide, setSlide] = useState(0);

  const adSlides = [
    { icon: Wallet, title: "Run the whole project in one place",
      body: "Budgets, FF&E, purchase orders and invoices — organized and always up to date for you and your client." },
    { icon: Palette, title: "Mood boards that come to life",
      body: "Pull inspiration straight from Pinterest and build paint palettes with Benjamin Moore, Sherwin-Williams and Farrow & Ball." },
    { icon: Users, title: "Keep clients beautifully in sync",
      body: "Share scheduling, notes and approvals — with intuitive dashboards for designer and client." },
  ];
  const slideCount = adSlides.length + 1; // original text + ad slides

  useEffect(() => {
    let interval;
    const first = setTimeout(() => {
      setSlide(1);
      interval = setInterval(() => setSlide((s) => (s + 1) % slideCount), 10000);
    }, 10000);
    return () => { clearTimeout(first); if (interval) clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register({ name: form.name, email: form.email, password: form.password, role: form.role });
      }
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Visual */}
      <div className="relative hidden lg:flex flex-col items-center justify-center text-center bg-white border-r border-[#EBE5E0] p-12 overflow-hidden">
        {/* Slide 0 — original brand text */}
        <div data-testid="login-slide-0"
          className={`absolute inset-0 flex flex-col items-center justify-center px-12 transition-opacity duration-700 ${slide === 0 ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          <h1 className="font-serif text-7xl text-[#A68A6B] leading-none mb-6">WorkRoom</h1>
          <h2 className="font-serif text-3xl text-[#2B2622] leading-tight max-w-md">
            Streamlined project management for interior design
          </h2>
          <p className="font-serif italic text-2xl text-[#A68A6B] mt-10">admin made beautiful</p>
        </div>

        {/* Ad slides */}
        {adSlides.map((s, i) => {
          const Icon = s.icon;
          const idx = i + 1;
          return (
            <div key={idx} data-testid={`login-slide-${idx}`}
              className={`absolute inset-0 flex flex-col items-center justify-center px-12 transition-opacity duration-700 ${slide === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              <p className="text-xs uppercase tracking-[0.3em] text-[#A68A6B] mb-8">WorkRoom</p>
              <div className="w-14 h-14 border border-[#D9CBBF] flex items-center justify-center mb-7">
                <Icon size={24} strokeWidth={1.25} className="text-[#A68A6B]" />
              </div>
              <h2 className="font-serif text-3xl xl:text-[2rem] text-[#2B2622] leading-tight whitespace-nowrap mb-5">{s.title}</h2>
              <p className="text-[#5C5651] text-base max-w-sm leading-relaxed">{s.body}</p>
            </div>
          );
        })}

        {/* Slide indicators */}
        <div className="absolute bottom-16 left-0 right-0 flex items-center justify-center gap-2">
          {Array.from({ length: slideCount }).map((_, i) => (
            <button key={i} data-testid={`slide-dot-${i}`} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${slide === i ? "w-6 bg-[#A68A6B]" : "w-1.5 bg-[#D9CBBF] hover:bg-[#A68A6B]"}`} />
          ))}
        </div>

        {/* Persistent footer */}
        <p data-testid="login-feature-list" className="absolute bottom-8 left-0 right-0 px-12 text-center text-[11px] tracking-wide text-[#A68A6B]/80">
          Budgets . Approvals . FF&amp;E . POs . Shared Notes . Scheduling . Mood Boards . Design Schemes
        </p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center px-6 py-16 sm:px-16">
        <div className="w-full max-w-sm animate-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-[#A68A6B] mb-2">WorkRoom</p>
          <h1 className="font-serif text-4xl text-[#2B2622] mb-1">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h1>
          <p className="text-[#5C5651] text-sm mb-8">
            {mode === "login" ? "Sign in to your studio workspace." : "Join your studio workspace."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Full name</Label>
                <Input data-testid="register-name-input" value={form.name} onChange={upd("name")} required
                  className="mt-1 rounded-none border-0 border-b border-[#EBE5E0] bg-transparent focus-visible:ring-0 focus-visible:border-[#A68A6B] px-0" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Email</Label>
              <Input data-testid="login-email-input" type="email" value={form.email} onChange={upd("email")} required
                className="mt-1 rounded-none border-0 border-b border-[#EBE5E0] bg-transparent focus-visible:ring-0 focus-visible:border-[#A68A6B] px-0" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Password</Label>
              <Input data-testid="login-password-input" type="password" value={form.password} onChange={upd("password")} required
                className="mt-1 rounded-none border-0 border-b border-[#EBE5E0] bg-transparent focus-visible:ring-0 focus-visible:border-[#A68A6B] px-0" />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5651] mb-2 block">I am a</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["designer", "client"].map((r) => (
                    <button type="button" key={r} data-testid={`role-${r}-btn`}
                      onClick={() => setForm({ ...form, role: r })}
                      className={`py-2 text-sm capitalize border transition-colors ${form.role === r ? "bg-[#A68A6B] text-white border-[#A68A6B]" : "border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B]"}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="login-submit-button" type="submit" disabled={busy}
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white py-6 text-sm uppercase tracking-wider">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[#5C5651] text-center">
            {mode === "login" ? "New to WorkRoom?" : "Already have an account?"}{" "}
            <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#A68A6B] hover:text-[#8C7154] underline underline-offset-4">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
